import { useState } from 'react'

import { Box, Typography, Button, Card, Switch, Breadcrumbs } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import {
  Link2,
  QrCode,
  Zap,
  Download,
  Settings,
  RefreshCw,
  Database,
  Bell,
  Check,
  Tag,
  ArrowLeft,
} from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { SectionHeader, InfoRow, StatusBadge } from '../../../common/components'
import { formatDateOnly } from '../../../common/functions/timeConversion'
import { useTimezone } from '../../../utils/datetime'
import AssetTagHistoryTable from '../Devices/AssetTagHistoryTable'

import { getAssetTagByUuid } from './api'

// StatusBadge color choices — keyed by lowercased couplingStatus so the
// "Coupled"/"Empty" pill color tracks the asset-tag's actual state.
const COUPLING_STATUS_COLOR = {
  coupled: 'green',
  empty: 'gray',
}

// Same enum-to-label map used by VTagsTable so the V-Tag Details card
// renders "External Module" instead of the raw `EXTERNAL_MODULE`
// envelope. Mirrored locally (not exported from the table module) to
// keep this page self-contained — if a fourth type is added, both
// maps need to be updated together.
const TYPE_LABEL = {
  DEVICE: 'Device',
  EXTERNAL_MODULE: 'External Module',
  INPUT_MODULE: 'Input Module',
}
const formatVTagType = (rawType) => (rawType ? (TYPE_LABEL[rawType] ?? rawType) : '--')

const InfoGrid = ({ items }) => (
  <Box className="info-grid gap-3">
    {items.map(({ label, value }) => (
      <Card
        key={label}
        sx={{
          bgcolor: 'var(--subcard-bg)',
          p: 1.5,
        }}
      >
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {label}
        </Typography>
        <Typography variant="h6" color="text.primary" fontWeight={500}>
          {value}
        </Typography>
      </Card>
    ))}
  </Box>
)

export default function VTagDetail() {
  // Re-render this page when the admin changes the Global Settings
  // timezone, so every timestamp below re-formats immediately.
  useTimezone()
  const { uuid } = useParams()
  const navigate = useNavigate()

  // Fetch the V-Tag details from /asset-tags/{uuid}. The response body is
  // wrapped in `{ success, message, data }`; the V-Tag object lives at
  // `data`. Anything not in the API response falls back to the existing
  // mock data in the JSX so the page layout is preserved.
  const { data: vTagResponse } = useQuery({
    queryKey: ['asset-tag', uuid],
    queryFn: () => getAssetTagByUuid(uuid),
    enabled: Boolean(uuid),
  })
  const vTag = vTagResponse?.data?.data ?? vTagResponse?.data ?? null

  const [publicView, setPublicView] = useState(Boolean(vTag?.qrEnabled))
  // Keep the toggle in sync if the fetched V-Tag changes its qrEnabled flag
  // after the initial render (e.g., the user navigates between V-Tags).
  if (vTag && publicView !== Boolean(vTag.qrEnabled) && vTag.qrEnabled !== undefined) {
    setPublicView(Boolean(vTag.qrEnabled))
  }

  // Derived values that fall back to existing mock strings when the API
  // hasn't returned a value yet (first paint, refetch, error).
  const titleText = vTag?.assetTagSerial ?? 'AT-2014'
  const statusKey = vTag?.couplingStatus?.toLowerCase()
  const statusLabel = statusKey ? statusKey.charAt(0).toUpperCase() + statusKey.slice(1) : 'Coupled'
  const statusColor = statusKey ? (COUPLING_STATUS_COLOR[statusKey] ?? 'green') : 'green'
  const facilityText = vTag?.facilityId != null ? `Facility ${vTag.facilityId}` : null
  // Sensor names attached to the coupled device, joined into a single
  // human-readable string for the "Sensor name" row in the Current Coupling
  // card. Falls back to the original mock label when the API hasn't returned
  // any sensors yet.
  const sensorNamesText =
    (vTag?.activeDevice?.sensors ?? [])
      .map((s) => s?.name)
      .filter(Boolean)
      .join(', ') || '--'

  return (
    <div className="sensor-page">
      {/* Top Bar */}
      <div className="sensor-topbar">
        <div>
          {/* Sits above the breadcrumb so the user can return to the
              V-Tags listing without using the browser's back button.
              Uses navigate(-1) so the listing's scroll position, search
              input, and active filters are preserved — same pattern as
              DeviceDetail. */}
          <Button
            size="small"
            variant="text"
            color="primary"
            startIcon={<ArrowLeft size={14} />}
            onClick={() => navigate(-1)}
            sx={{
              mb: 1.5,
              ml: -1,
              textTransform: 'none',
              // Subtle primary-tinted rectangle on hover, darkening to a
              // deeper shade so the click target reads as a square button.
              '&:hover': {
                backgroundColor: 'rgba(59, 130, 246, 0.24)',
              },
            }}
          >
            Back
          </Button>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
            <Link underline="hover" color="inherit" href="/sensors">
              <div>Sensors &nbsp; &gt; &nbsp; V-Tags</div>
            </Link>
            <Typography variant="body2" color="text.secondary">
              {titleText}
            </Typography>
          </Breadcrumbs>
          <div className="flex items-center gap-3 mb-1">
            <Typography variant="h5">{titleText}</Typography>
            <StatusBadge color={statusColor} dot>
              {statusLabel}
            </StatusBadge>
          </div>
          <Typography color="text.secondary" variant="subtitle2" fontWeight={400}>
            {facilityText
              ? `${facilityText} · Cold Room A · 3 device swaps · monitoring since 04 Jan 2024`
              : 'Jebdan · Cold Room A · 3 device swaps · monitoring since 04 Jan 2024'}
          </Typography>
        </div>
        {/* <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outlined" startIcon={<ArrowLeftRight size={14} />}>
            Swap device
          </Button>
          <Button variant="contained" color="error" startIcon={<Unlink2 size={14} />}>
            Decouple
          </Button>
        </div> */}
      </div>

      {/* Body */}
      <div className="sensor-body">
        {/* LEFT COLUMN */}
        <div className="flex flex-col gap-5">
          {/* Current Coupling */}
          <Card className="p-5">
            <SectionHeader
              icon={Link2}
              label="Current Coupling"
              // Right-aligned `action` slot — keeps the "Open device
              // detail" button on the same row as the header instead of
              // stacking below the InfoGrid. Disabled when the V-Tag
              // isn't coupled (`activeDevice.uuid` is null) so a stray
              // click on an empty V-Tag doesn't push
              // `/sensors/details/undefined`.
              action={
                <Button
                  size="small"
                  variant="outlined"
                  className="sensor-btn-tiny"
                  disabled={!vTag?.activeDevice?.uuid}
                  onClick={() => navigate(`/sensors/details/${vTag.activeDevice.uuid}`)}
                >
                  Open device detail
                </Button>
              }
            />
            <InfoGrid
              items={[
                { label: 'Coupled device', value: vTag?.activeDevice?.serialNumber ?? '--' },
                {
                  label: 'Vendor / model',
                  value: vTag?.activeDevice?.manufacturer?.name ?? 'Kelsius Fx-200',
                },
                {
                  label: 'Coupled since',
                  value: formatDateOnly(vTag?.activeDevice?.coupledAt) || '03 Dec 2025',
                },
                { label: 'Sensor name', value: sensorNamesText },
              ]}
            />
          </Card>

          {/* Swap History */}
          <Card className="p-5">
            <SectionHeader icon={RefreshCw} label="Swap History — Full Lineage" />
            <AssetTagHistoryTable uuid={uuid} />
          </Card>

          {/* Linked Alarm Profiles */}
          <Card className="p-5">
            <SectionHeader
              icon={Zap}
              label={
                <>
                  Linked Alarm Profiles <span className="section-header__count">· 2 of 4</span>
                </>
              }
              action={
                <Button size="small" variant="outlined" className="sensor-btn-tiny">
                  Manage
                </Button>
              }
            />
            <div className="sensor-alarm-list">
              <div className="sensor-alarm">
                <div className="sensor-alarm__left">
                  <span className="sensor-alarm__icon sensor-alarm--critical">
                    <Bell size={18} />
                  </span>
                  <div>
                    <Typography variant="subtitle2" fontWeight={700}>
                      High Temp — Critical
                    </Typography>
                    <Typography variant="body1" fontWeight={400}>
                      Warning &gt; 5.0 °C · Critical &gt; 6.0 °C
                    </Typography>
                  </div>
                </div>
                <StatusBadge color="gray">Default</StatusBadge>
              </div>
              <div className="sensor-alarm">
                <div className="sensor-alarm__left">
                  <span className="sensor-alarm__icon sensor-alarm--active">
                    <Check size={18} />
                  </span>
                  <div>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Low Temp — Warning
                    </Typography>
                    <Typography variant="body1" fontWeight={400}>
                      Warning &lt; 2.0 °C
                    </Typography>
                  </div>
                </div>
                <StatusBadge color="green">Active</StatusBadge>
              </div>
            </div>
          </Card>
        </div>

        {/* RIGHT COLUMN */}
        <div className="flex flex-col gap-5">
          {/* V-Tag Details — identifying attributes of the asset tag
              itself (name, serial, coupling status, swap count, type).
              All values are read directly from the asset-tag DTO fetched
              via `getAssetTagByUuid`; falls back to `--` before the
              query resolves so the card never reads the dummy strings
              once data is available. */}
          <Card className="p-5">
            <SectionHeader icon={Tag} label="V-Tag Details" />
            <InfoRow label="Name" value={vTag?.name ?? '--'} />
            <InfoRow label="Serial" value={vTag?.assetTagSerial ?? '--'} />
            <InfoRow
              label="Coupling Status"
              value={
                <StatusBadge color={statusColor} dot>
                  {statusLabel}
                </StatusBadge>
              }
            />
            <InfoRow label="Swap Count" value={vTag?.swapCount ?? '--'} />
            <InfoRow label="Type" value={formatVTagType(vTag?.type)} />
          </Card>

          {/* Public QR */}
          <Card className="p-5">
            <SectionHeader icon={QrCode} label="Public QR" />
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box className="qr-container">
                <svg
                  viewBox="0 0 100 100"
                  width="72"
                  height="72"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {[...Array(10)].map((_, r) =>
                    [...Array(10)].map((_, c) => {
                      const on = (r + c * 3 + r * c) % 3 !== 0
                      return on ? (
                        <rect
                          key={`${r}-${c}`}
                          x={c * 10}
                          y={r * 10}
                          width={9}
                          height={9}
                          fill="#111425"
                        />
                      ) : null
                    })
                  )}
                  {[
                    [0, 0],
                    [0, 70],
                    [70, 0],
                  ].map(([x, y], i) => (
                    <g key={i}>
                      <rect x={x} y={y} width={30} height={30} fill="#111425" />
                      <rect x={x + 4} y={y + 4} width={22} height={22} fill="white" />
                      <rect x={x + 8} y={y + 8} width={14} height={14} fill="#111425" />
                    </g>
                  ))}
                </svg>
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  Public view-only page
                </Typography>
                <Typography variant="body1">
                  Scan to show this AssetTag's current readings — no login. Disabled by default.
                </Typography>
                <Box className="flex items-center gap-2 mt-2">
                  <Switch checked={publicView} onChange={(e) => setPublicView(e.target.checked)} />
                  <Typography variant="body2" color="text.secondary">
                    {publicView ? 'Enabled' : 'Disabled'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Card>

          {/* Calibration */}
          <Card className="p-5">
            <SectionHeader icon={Settings} label="Calibration" />
            <InfoRow label="Current cert" value="KL-22014-08" />
            <InfoRow label="Type" value="Accredited" />
            <InfoRow
              label="Expiry"
              value={<StatusBadge color="green">Valid · 02 Dec 28</StatusBadge>}
            />

            <Box
              sx={{
                bgcolor: 'var(--chip-filled-bg)',
                borderRadius: 2,
                p: 1.5,
                my: 1.5,
              }}
            >
              <Typography
                sx={{ fontSize: '0.72rem', color: 'var(--input-placeholder)', lineHeight: 1.6 }}
              >
                Showing the{' '}
                <Box component="span" sx={{ fontWeight: 700, color: 'var(--card-text)' }}>
                  current
                </Box>{' '}
                device's certificate. All previous devices' calibration records remain retained
                under this AssetTag.
              </Typography>
            </Box>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<Download size={14} />}
              // Reuse the same anchor-button pattern from DeviceDetail: a
              // native `<a>` triggers the browser's built-in download flow
              // (works for presigned / signed URLs without needing a
              // Content-Disposition header). Disabled when the V-Tag has
              // no coupled device OR the active device has no certificate
              // attached — clicking an empty href would push
              // `undefined` to the URL bar.
              component="a"
              href={vTag?.activeDevice?.calibrationCertificateUrl || undefined}
              target="_blank"
              rel="noopener noreferrer"
              download
              disabled={!vTag?.activeDevice?.calibrationCertificateUrl}
            >
              Download current certificate
            </Button>
          </Card>

          {/* Continuity */}
          <Card className="p-5">
            <SectionHeader icon={Database} label="Continuity" />
            <InfoRow label="Data retention" value="5+ years" />
            <InfoRow
              label="Data gaps on swap"
              value={<StatusBadge color="green">None</StatusBadge>}
            />
            <InfoRow label="Records on this tag" value="3 devices" />
          </Card>
        </div>
      </div>
    </div>
  )
}
